import { useState } from 'react';
import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { Skeleton } from '../components/Skeleton';
import { PushOptIn } from '../components/PushOptIn';
import { pushTopicLabels } from '../lib/pushTopics';
import { TestIntro } from '../components/TestIntro';
import { ShowAllToggle } from '../components/ShowAllToggle';
import { paperTestSummary } from '../lib/paperTestPages';
import { useBreakoutWatch } from '../hooks/useBreakoutWatch';
import { ApiClientError, type BreakoutWatchResponse } from '../lib/apiClient';
import {
  BREAKOUT_COSTS,
  BREAKOUT_RULE,
  isCrater,
  isMoon,
  type BreakoutOutcome,
  type BreakoutTrade,
  type BreakoutVerdict,
  type BreakoutWatchRow,
} from '../lib/breakoutWatch';
import { netReturn } from '../lib/crashBounce';
import { formatSignedUsd } from '../lib/format';
// The two paper tests share one card layout; this page adds its own pieces.
import base from './CrashBouncePage.module.css';
import own from './BreakoutWatchPage.module.css';

const HOUR_MS = 3_600_000;
const RULE = BREAKOUT_RULE;

const pct = (x: number | null, digits = 1) =>
  x === null ? '—' : `${(x * 100).toFixed(digits)}%`;
const signedPct = (x: number | null, digits = 1) =>
  x === null
    ? '—'
    : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x * 100).toFixed(digits)}%`;
const price = (x: number | null) =>
  x === null
    ? '—'
    : `$${x.toLocaleString(undefined, { maximumSignificantDigits: 4, minimumSignificantDigits: 2 })}`;
const day = (ms: number) =>
  new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const VERDICT_LABEL: Record<BreakoutVerdict, string> = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
};

const STATUS_LABEL: Record<BreakoutWatchRow['status'], string> = {
  watching: 'Watching',
  pending: 'Buying next hour',
  holding: 'Holding',
  cooldown: 'Cooling off',
  'no data': 'No data',
};

const REASON_LABEL: Record<NonNullable<BreakoutTrade['reason']>, string> = {
  target: `+${pct(RULE.targetPct, 0)} target`,
  stop: `−${pct(RULE.stopPct, 0)} stop`,
  time: '30-day limit',
};

export function BreakoutWatchPage() {
  const query = useBreakoutWatch();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Breakout Watch data is temporarily unavailable.';
  return (
    <div className={base.page}>
      <TestIntro
        heading={
          <div className={base.heading}>
            <h1>Breakout Watch</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/breakout-watch')}
      >
        <p>
          A paper test of one breakout rule across {RULE.coins.length} large coins: when a
          coin closes an hour above its highest price of the previous 3 days, after those
          3 days stayed within a {pct(RULE.rangePct, 0)} range, on at least {RULE.volMult}
          × its normal daily volume, buy at the next hour&apos;s open, then sell at +
          {pct(RULE.targetPct, 0)}, at −{pct(RULE.stopPct, 0)}, or after 30 days.
        </p>
        <p>
          <strong>This rule failed its backtest.</strong> From 2025 on it broke about even
          after fees. It&apos;s tracked because its signals were followed by a +30% week
          about 3× more often than equally volatile hours were. This page checks both
          things from here on: the trades, and the weeks after each signal.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing on this page places an order. Breakout Watch is a research experiment, not a recommendation to buy or sell anything. Most breakouts go nowhere, and this one didn't make money in testing."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading Breakout Watch…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="14rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          <Scorecard data={data} />
          <PushOptIn topic="breakout-watch" {...pushTopicLabels('breakout-watch')} />
          <BigWeeks data={data} />
          <OpenPositions rows={data.watchlist.filter((r) => r.open)} />
          <Watchlist rows={data.watchlist} />
          <RecentSignals outcomes={data.outcomes} />
          <ClosedTrades trades={data.trades} />
          <p className={base.fine}>
            Prices are public Coinbase hourly candles, checked five minutes after each
            hour. &ldquo;Normal daily volume&rdquo; is the median of the 30 days before
            the last 24 hours. Each trade is scored with{' '}
            {pct(BREAKOUT_COSTS.slippagePerSide, 2)} slippage per fill and a fee of{' '}
            {pct(BREAKOUT_COSTS.feeHigh, 1)} per side, or {pct(BREAKOUT_COSTS.feeLow, 1)}{' '}
            where noted. Rule locked {dayTime(Date.parse(data.registeredAt))}; only
            signals after that count.{' '}
            {data.lastRunMs
              ? `Last checked ${dayTime(data.lastRunMs)}.`
              : 'Waiting for the first hourly check.'}
            {data.lastFailures.length > 0 &&
              ` Couldn't read ${data.lastFailures.join(', ')} on that check; they catch up on the next.`}
          </p>
        </>
      )}
    </div>
  );
}

function Scorecard({ data }: { data: BreakoutWatchResponse }) {
  const s = data.summary;
  const bar = data.passBar;
  return (
    <section className={base.card} aria-labelledby="bw-score-heading">
      <div className={base.cardHead}>
        <h2 id="bw-score-heading">Forward test: trades</h2>
        <span className={`${base.verdict} ${base[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={base.progress}>
        {s.trades}/{bar.minTrades} trades · {s.weeks}/{bar.minWeeks} weeks
        {s.open > 0 && ` · ${s.open} open`}
      </p>
      {s.trades > 0 && (
        <dl className={base.stats}>
          <div>
            <dt>Win rate</dt>
            <dd>{pct(s.winRate)}</dd>
          </div>
          <div>
            <dt>Avg per trade</dt>
            <dd>{signedPct(s.meanNetHigh)}</dd>
          </div>
          <div>
            <dt>${data.stakeDollars.toLocaleString()}/trade P&amp;L</dt>
            <dd>{formatSignedUsd(s.pnlHigh)}</dd>
          </div>
        </dl>
      )}
      <p className={base.small}>
        {s.trades > 0 ? (
          <>
            At a {pct(BREAKOUT_COSTS.feeLow, 1)} fee: {signedPct(s.meanNetLow)} a trade,{' '}
            {formatSignedUsd(s.pnlLow)} total.{' '}
            {s.intervalHigh &&
              `Likely range of the average: ${signedPct(s.intervalHigh[0])} to ${signedPct(s.intervalHigh[1])}.`}
          </>
        ) : (
          'No trades yet. The backtest saw roughly one breakout trade a week across all coins, bunched into market-wide moves.'
        )}
      </p>
      <p className={base.small}>
        Passes after {bar.minTrades} trades across {bar.minWeeks} separate weeks if the
        average trade is above zero at a {pct(bar.feePerSide, 1)} fee and the low end of
        its likely range is too. Fails at once if a stop ever fills worse than −
        {pct(RULE.stopPct + bar.maxStopOvershoot, 0)}.
      </p>
    </section>
  );
}

function BigWeeks({ data }: { data: BreakoutWatchResponse }) {
  const s = data.summary;
  const bt = data.backtest;
  return (
    <section className={base.card} aria-labelledby="bw-weeks-heading">
      <h2 id="bw-weeks-heading">The week after a signal</h2>
      <p className={base.progress}>
        {s.signalsFinished} signal{s.signalsFinished === 1 ? '' : 's'} scored
        {s.signalsWatching > 0 && ` · ${s.signalsWatching} still inside their 7 days`}
      </p>
      <dl className={base.stats}>
        <div>
          <dt>Hit +{pct(RULE.moonPct, 0)}</dt>
          <dd>{pct(s.moonRate)}</dd>
        </div>
        <div>
          <dt>Backtest</dt>
          <dd>{pct(bt.moonRateAfterSignal)}</dd>
        </div>
        <div>
          <dt>Hit −{pct(RULE.craterPct, 0)}</dt>
          <dd>{pct(s.craterRate)}</dd>
        </div>
      </dl>
      <p className={base.small}>
        Share of signals where the coin traded at least {pct(RULE.moonPct, 0)} above the
        next hour&apos;s open at some point in the following 7 days, whether or not it was
        traded. In the backtest that happened after {pct(bt.moonRateAfterSignal)} of
        signals, against {pct(bt.moonRateVolMatched)} of equally volatile hours. It&apos;s
        a touch, not a close: most of those moves gave the gain back.
      </p>
    </section>
  );
}

function OpenPositions({ rows }: { rows: BreakoutWatchRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section className={base.card} aria-labelledby="bw-open-heading">
      <h2 id="bw-open-heading">Open paper positions</h2>
      <ul className={base.list}>
        {rows.map((r) => {
          const t = r.open!;
          const target = t.entryPx * (1 + RULE.targetPct);
          const stop = t.entryPx * (1 - RULE.stopPct);
          const deadline = t.entryMs + (RULE.maxHoldH - 1) * HOUR_MS;
          return (
            <li key={r.coin} className={base.row}>
              <div className={base.rowHead}>
                <strong>{r.coin}</strong>
                <span className={tone(r.unrealized)}>{signedPct(r.unrealized)}</span>
              </div>
              <p className={base.small}>
                Bought {dayTime(t.entryMs)} at {price(t.entryPx)} · now{' '}
                {price(r.lastClose)} · sells at {price(target)} or {price(stop)}, or by{' '}
                {day(deadline)}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Rows shown before "Show all": the closest to a signal, plus any active ones. */
const WATCHLIST_PREVIEW = 5;
/** States that stay listed even past the preview: something is happening. */
const PINNED = new Set(['pending', 'holding', 'cooldown']);

function Watchlist({ rows }: { rows: BreakoutWatchRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = rows.length > WATCHLIST_PREVIEW;
  const shown =
    expanded || !trimmed
      ? rows
      : rows.filter((r, i) => i < WATCHLIST_PREVIEW || PINNED.has(r.status));
  return (
    <section className={base.card} aria-labelledby="bw-watch-heading">
      <h2 id="bw-watch-heading">Closest to a signal</h2>
      <p className={base.small}>
        A buy needs all three on one hourly close: volume at least {RULE.volMult}× normal,
        the previous 3 days within a {pct(RULE.rangePct, 0)} range, and a close above
        their high. Green means that part is met.
      </p>
      <table id="bw-watchlist" className={own.table}>
        <thead>
          <tr>
            <th scope="col">Coin</th>
            <th scope="col">Volume</th>
            <th scope="col">3-day range</th>
            <th scope="col">vs 3-day high</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={r.coin}>
              <th scope="row">
                <span className={own.coin}>{r.coin}</span>
                {r.status !== 'watching' && (
                  <span className={own.status}>
                    {r.status === 'cooldown' && r.cooldownUntilMs
                      ? `Cooling off to ${day(r.cooldownUntilMs)}`
                      : STATUS_LABEL[r.status]}
                  </span>
                )}
              </th>
              <td>
                <Check
                  ok={r.volumeRatio !== null && r.volumeRatio >= RULE.volMult}
                  label={r.volumeRatio === null ? '—' : `${r.volumeRatio.toFixed(1)}×`}
                />
              </td>
              <td>
                <Check
                  ok={r.priorRange !== null && r.priorRange <= RULE.rangePct}
                  label={pct(r.priorRange, 0)}
                />
              </td>
              <td>
                <Check
                  ok={r.vsPriorHigh !== null && r.vsPriorHigh > 0}
                  label={signedPct(r.vsPriorHigh)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {trimmed && (expanded || shown.length < rows.length) && (
        <ShowAllToggle
          total={rows.length}
          expanded={expanded}
          onToggle={() => setExpanded((open) => !open)}
          controls="bw-watchlist"
        />
      )}
    </section>
  );
}

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`${own.check} ${ok ? own.checkOk : ''}`}>
      {label}
      {label !== '—' && (
        <span className="visually-hidden">{ok ? ', met' : ', not met'}</span>
      )}
    </span>
  );
}

function RecentSignals({ outcomes }: { outcomes: BreakoutOutcome[] }) {
  return (
    <section className={base.card} aria-labelledby="bw-signals-heading">
      <h2 id="bw-signals-heading">Scored signals</h2>
      {outcomes.length === 0 ? (
        <p className={base.small}>
          None yet. Each signal is scored 7 days after it fires.
        </p>
      ) : (
        <ul className={base.list}>
          {outcomes.slice(0, 50).map((o) => {
            const up = o.refPx === null ? null : o.maxHigh / o.refPx - 1;
            const down = o.refPx === null ? null : o.minLow / o.refPx - 1;
            const label = isMoon(o)
              ? `Hit +${pct(RULE.moonPct, 0)}`
              : isCrater(o)
                ? `Hit −${pct(RULE.craterPct, 0)}`
                : 'No big move';
            return (
              <li key={`${o.coin}-${o.signalBarMs}`} className={base.row}>
                <div className={base.rowHead}>
                  <strong>{o.coin}</strong>
                  <span
                    className={isMoon(o) ? base.up : isCrater(o) ? base.down : base.flat}
                  >
                    {label}
                  </span>
                </div>
                <p className={base.small}>
                  {day(o.signalBarMs)} · best {signedPct(up)} · worst {signedPct(down)}
                  {o.traded
                    ? ' · traded'
                    : ' · not traded (already holding or cooling off)'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function ClosedTrades({ trades }: { trades: BreakoutTrade[] }) {
  return (
    <section className={base.card} aria-labelledby="bw-closed-heading">
      <h2 id="bw-closed-heading">Closed paper trades</h2>
      {trades.length === 0 ? (
        <p className={base.small}>None yet.</p>
      ) : (
        <ul className={base.list}>
          {trades.slice(0, 50).map((t) => {
            const net =
              t.exitPx === undefined
                ? null
                : netReturn(t.entryPx, t.exitPx, BREAKOUT_COSTS.feeHigh);
            return (
              <li key={`${t.coin}-${t.entryMs}`} className={base.row}>
                <div className={base.rowHead}>
                  <strong>{t.coin}</strong>
                  <span className={tone(net)}>{signedPct(net)}</span>
                </div>
                <p className={base.small}>
                  {day(t.entryMs)} → {t.exitMs ? day(t.exitMs) : '—'} · {price(t.entryPx)}{' '}
                  → {price(t.exitPx ?? null)} · {t.reason ? REASON_LABEL[t.reason] : ''}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || x === 0) return base.flat;
  return x > 0 ? base.up : base.down;
}
